import Card from '@/components/shared/Card';
import Pagination from '@/components/shared/Pagination';
import useSlidesPerView from '@/hooks/use-slides-per-view';
import { Product } from '@/types';
import classNames from 'classnames';
import { MutableRefObject, useEffect, useRef, useState } from 'react';
import { Swiper, SwiperRef, SwiperSlide } from 'swiper/react';

type Props = {
    products: Product[];
};

const ArticleProductsList = ({ products }: Props) => {
    const swiperRef = useRef<SwiperRef>();
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isFirstRender, setIsFirstRender] = useState(true);
    const slidesPerView = useSlidesPerView({ desktop: 2.05, tablet: 2.1, defaultCount: 1.09 });

    useEffect(() => {
        const timeout = setTimeout(() => {
            setIsFirstRender(false);
        }, 1);

        return () => clearTimeout(timeout);
    }, []);

    return (
        <div className="article-product-list-wrapper">
            {isFirstRender || products.length <= slidesPerView ? (
                <ul className="article-product-list list-unstyled">
                    {products.map((item, i) => (
                        <li
                            key={i}
                            className={classNames('article-product-item', {
                                'article-product-item--single': products.length === 1,
                                'article-product-item--duo': products.length === 2,
                            })}
                        >
                            <Card
                                cardName={item.name}
                                productId={item.id}
                                materialName={(item.materials || []).map((obj) => obj.name).join('<br>')}
                                state={item.state}
                                productColor={item.color}
                                categoryId={item.category_id}
                                slug={item.slug}
                                price={item.price}
                                images={item.images}
                                reference={item.reference}
                                diameter={item.diameter}
                                isStandardForm={item.is_standard_form}
                                size={item.size}
                                accessories={item.accessories}
                                type={item.type}
                                params={item.params}
                                insert={item.insert}
                                available={item.available}
                            />
                        </li>
                    ))}
                </ul>
            ) : (
                <Swiper
                    slidesPerView={1.09}
                    ref={swiperRef as MutableRefObject<SwiperRef>}
                    onActiveIndexChange={(swiper) => {
                        setCurrentIndex(swiper.realIndex);
                    }}
                    spaceBetween={2}
                    speed={1000}
                    centeredSlides
                    longSwipesRatio={0.01}
                    breakpoints={{
                        1200: {
                            slidesPerView: 2.05,
                        },
                        768: {
                            slidesPerView: 2.1,
                        },
                    }}
                >
                    {products.map((item, i) => (
                        <SwiperSlide key={i}>
                            <Card
                                cardName={item.name}
                                productId={item.id}
                                materialName={(item.materials || []).map((obj) => obj.name).join('<br>')}
                                state={item.state}
                                productColor={item.color}
                                categoryId={item.category_id}
                                slug={item.slug}
                                price={item.price}
                                images={item.images}
                                reference={item.reference}
                                diameter={item.diameter}
                                isStandardForm={item.is_standard_form}
                                size={item.size}
                                accessories={item.accessories}
                                type={item.type}
                                params={item.params}
                                insert={item.insert}
                                available={item.available}
                            />
                        </SwiperSlide>
                    ))}
                </Swiper>
            )}

            {!isFirstRender && products.length > slidesPerView && (
                <div className="articles-products-slider-pagination">
                    <Pagination
                        length={products.length}
                        page={currentIndex + 1}
                        onPaginationChange={(number) => {
                            swiperRef.current?.swiper.slideToLoop(number - 1);
                        }}
                    />
                </div>
            )}
        </div>
    );
};

export default ArticleProductsList;
